<template>
  <div id="project-navbar" class="dashboards">
    <div v-if="!showMobileMenu" class="resp-menu">
      <div
        v-for="r in dashboardsWithStaticRoutes"
        v-show="r.respMenuCondition || (r.dashboard && route.name == dashboardsLink) "
        :key="r.display_name+r.name"
        class="top-menu-name">
        <div v-if="r.display_name === $t('Executive Analysis')" class="executive-analysis">
          <StoreDrilldownIcon />
        </div>
        <i v-else :class="r.icon" />
        <small>{{ r.dashboard ? getDashboardName(r.name) : $t(r.display_name) }}</small>
      </div>
      <i class="mdi mdi-menu"
         @click="showMMenu" />
    </div>

    <div v-if="showMobileMenu" class="menu-mobile">
      <div class="menu-close">
        <i class="mdi mdi-close" @click="showMMenu" />
      </div>

      <router-link :to="{ name: 'projects' }">
        <div class="backToProjects">
          <i class="mdi mdi-arrow-left" />
          <span>{{ $t('Projects') }}</span>
        </div>
      </router-link>
      <template v-for="r in dashboardsWithStaticRoutes">
        <router-link
          v-if="r.condition || r.dashboard"
          :key="r.display_name+r.name"
          :to="getDashboardLink(r)"
          class="dashboard"
          @click.native="showMMenu">
          <div v-if="r.display_name === $t('Executive Analysis')" class="executive-analysis">
            <StoreDrilldownIcon />
          </div>
          <i v-else :class="r.icon" />
          <small>{{ r.dashboard ? getDashboardName(r.name) : $t(r.display_name) }}</small>
        </router-link>
      </template>
    </div>

    <div class="menu-web">
      <div class="menu-close">
        <i class="mdi mdi-close" @click="showMMenu" />
      </div>

      <template v-for="r in dashboardsWithStaticRoutes">
        <router-link
          v-if="(r.condition && !limitedAccessDashboardMenus.find(item => item == startCase(r.display_name.toLowerCase()))) || r.dashboard"
          :key="r.display_name+r.name"
          :to="getDashboardLink(r)"
          class="dashboard">
          <StoreDrilldownIcon v-if="r.display_name === 'Executive Analysis'" />
          <i v-else :class="r.icon" />
          <small>{{ r.dashboard ? getDashboardName(r.name) : $t(r.display_name) }}</small>
        </router-link>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { result, chain, startCase } from 'lodash';
import i18n from '@/i18n';
import StoreDrilldownIcon from './StoreDrilldownIcon';

export default {
  name: 'ProjectDashboards',
  components: {
    StoreDrilldownIcon
  },
  props: {
    internationalization: {
      type: Object,
      default() {
        return {};
      }
    },
    project: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showMobileMenu: false,
      map: 'map',
      coverage: 'coverage',
      team: 'auditors',
      stores: 'stores',
      storeShow: 'store-show',
      reports: 'reports',
      visits: 'visits',
      price: 'price',
      visitShow: 'visit-show',
      operations: 'operations',
      sku: 'sku',
      skuDetail: 'sku-availability-detail',
      executiveAnalysis: 'stores-with-tag-hierarchy',
      social: 'social',
      dashboardsLink: 'dashboards',
      executiveViewSummary: 'executive-view-summary',
      executiveViewCategory: 'executive-view-category',
      executiveViewKpi: 'executive-view-kpi',
      imageNavigator: 'image-navigator',
      startCase
    };
  },
  computed: {
    ...mapState(['Account', 'route']),
    disabledDashboardMenus() {
      return chain(this.project).result('settings.disabled_dashboard_menus', {}).value();
    },
    isCoverageDashboardActive() {
      const { coverage_dashboard } = this.project.settings;

      return coverage_dashboard && coverage_dashboard.enabled && coverage_dashboard.enabled !== 'false';
    },
    limitedAccessDashboardMenus() {
      return this.Account.active.data.limited_access ? chain(this.project).result('settings.limited_access', []).value() : [];
    },
    projectId() {
      return this.project.id;
    },
    priceAggregations() {
      return chain(this.project).result('price_aggregations').value();
    },
    socialControl() {
      return chain(this.project).result('settings.social').value();
    },
    dashboards() {
      return chain(this.project).result('dashboards', [])
        .sortBy('position')
        .value();
    },
    dashboardsWithStaticRoutes() {
      const { route } = this;
      const staticRoutes = [
        {
          respMenuCondition: route.name === this.social,
          condition: !this.disabledDashboardMenus.social && this.socialControl,
          icon: 'mdi mdi-thumb-up',
          display_name: this.$t('Social'),
          breadCrumb: 'social'
        },
        {
          respMenuCondition: route.name === this.executiveViewSummary || route.name === this.executiveViewCategory || route.name === this.executiveViewKpi,
          condition: !this.disabledDashboardMenus.executive_view,
          icon: 'mdi mdi-chart-multiline',
          display_name: this.$t('Executive View'),
          breadCrumb: 'executiveView'
        },
        {
          respMenuCondition: route.name === this.executiveAnalysis,
          condition: !this.disabledDashboardMenus.store_drilldown,
          icon: 'store-drill-down',
          display_name: this.$t('Executive Analysis'),
          breadCrumb: 'storeDrilldown'
        },
        {
          respMenuCondition: route.name === this.map,
          condition: !this.disabledDashboardMenus.map,
          icon: 'mdi mdi-map',
          display_name: this.$t('MAP'),
          breadCrumb: 'map'
        },
        {
          respMenuCondition: route.name === this.coverage,
          condition: !this.disabledDashboardMenus.coverage && this.isCoverageDashboardActive,
          icon: 'mdi mdi-format-list-bulleted',
          display_name: this.$t('COVERAGE'),
          breadCrumb: 'coverage'
        },
        {
          respMenuCondition: route.name === this.team,
          condition: !this.disabledDashboardMenus.team,
          icon: 'mdi mdi-account-multiple',
          display_name: this.$t('TEAM'),
          breadCrumb: 'auditors'
        },
        {
          respMenuCondition: [this.sku, this.skuDetail].includes(route.name),
          condition: !this.disabledDashboardMenus.sku,
          icon: 'mdi mdi-bottle-wine',
          display_name: this.$t('SKUs'),
          breadCrumb: 'sku'
        },
        {
          respMenuCondition: route.name === this.visits || route.name === this.visitShow,
          condition: !this.disabledDashboardMenus.visits,
          icon: 'mdi mdi-map-marker',
          display_name: this.$t('VISITS'),
          breadCrumb: 'visits'
        },
        {
          respMenuCondition: route.name === this.price,
          condition: !this.disabledDashboardMenus.price && this.priceAggregations.length,
          icon: 'mdi mdi-currency-usd',
          display_name: this.$t('Price'),
          breadCrumb: 'price'
        },
        {
          respMenuCondition: route.name === this.stores || route.name === this.storeShow,
          condition: !this.disabledDashboardMenus.stores,
          icon: 'mdi mdi-store',
          display_name: this.$t('STORES'),
          breadCrumb: 'stores'
        },
        {
          respMenuCondition: route.name === this.reports,
          condition: !this.disabledDashboardMenus.report,
          icon: 'mdi mdi-content-paste',
          display_name: this.$t('REPORTS'),
          breadCrumb: 'reports'
        },
        {
          respMenuCondition: route.name === this.operations,
          condition: !this.disabledDashboardMenus.operations,
          icon: 'mdi mdi-ship-wheel',
          display_name: this.$t('OPERATIONS'),
          breadCrumb: 'operations'
        },
        {
          respMenuCondition: route.name === this.imageNavigator,
          condition: !this.disabledDashboardMenus.image_navigator,
          icon: 'mdi mdi-image-search-outline',
          display_name: this.$t('IMAGE NAVIGATOR'),
          breadCrumb: 'imageNavigator'
        }
      ];
      const filterStaticRoutes = staticRoutes.filter(staticRoute => staticRoute.condition);

      this.dashboards.forEach((dashboard) => {
        filterStaticRoutes.splice(dashboard.position - 1, 0, { ...dashboard, dashboard: true });
      });

      return filterStaticRoutes;
    }
  },
  methods: {
    getDashboardName(name) {
      const translations = JSON.parse(localStorage.getItem('translations'));
      const translatedName = result(translations, `${i18n.locale}.${name}`);

      if (translatedName) {
        return translatedName;
      }

      return this.normalizeString(name);
    },
    getDashboardLink(dashboard) {
      const { projectId } = this;

      if (dashboard.dashboard) {
        return {
          name: this.Breadcrumbs.dashboard, params: { projectId, dashboardId: dashboard.id }
        };
      }

      return { name: this.Breadcrumbs[dashboard.breadCrumb], params: { projectId } };
    },
    showMMenu() {
      this.showMobileMenu = !this.showMobileMenu;
    }
  }
};
</script>

<style lang="scss">
.dashboards {
  flex-grow: 4;
  text-transform: uppercase;

  .dashboard {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 50px;
    font-size: 12px;
    font-weight: 600 !important;
    text-align: center;
    text-decoration: none;
    transition: background-color 0.3s;

    i {
      font-size: 36px;
    }
  }

  &.vertical {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    border-top: 2px solid $baiSeWhite;

    @include breakpoint(lg-min) {
      background-color: $andreaBlue;
    }

    @include breakpoint(lg-max) {
      display: block;
      justify-content: center;
      width: 100%;
    }

    .dashboard {
      width: 80px;
      height: 80px;
      margin-bottom: 3px;
      text-align: center;

      small {
        display: inline-block;
        padding: 0 5px;
      }
    }
  }

  &.horizontal {
    display: flex;

    .dashboard {
      padding: 0 15px;
      margin-right: 1px;
      background: $baiSeWhite;
    }
  }

  &.flat {
    .dashboard {
      color: $darkSkyBlue;
      background: $baiSeWhite;

      .store-drilldown-icon {
        &-first-path {
          fill: $andreaBlue;
        }

        &-second-path {
          stroke: $andreaBlue;
        }
      }

      &:hover {
        color: $white !important;
        background-color: $andreaBlue;
        transition: all 0.3s;

        .store-drilldown-icon {
          &-first-path {
            fill: $white;
          }

          &-second-path {
            stroke: $white;
          }
        }
      }
    }
  }

  &.invert {
    .dashboard {
      &.router-link-active,
      &.router-link-exact-active {
        color: $darkSkyBlue;
        background-color: $white;

        .store-drilldown-icon {
          &-first-path {
            fill: $darkSkyBlue;
          }

          &-second-path {
            stroke: $darkSkyBlue;
          }
        }
      }

      color: $baiSeWhite;
      background-color: $darkSkyBlue;

      .store-drilldown-icon {
        &-first-path {
          fill: $baiSeWhite;
        }

        &-second-path {
          stroke: $baiSeWhite;
        }
      }

      &:hover {
        color: $darkSkyBlue;
        background-color: $white;

        .store-drilldown-icon {
          &-first-path {
            fill: $darkSkyBlue;
          }

          &-second-path {
            stroke: $darkSkyBlue;
          }
        }
      }
    }
  }
}

.menu-close {
  display: none;
  justify-content: flex-end;

  i {
    font-size: 30px;
    color: $white;
  }
}

.resp-menu {
  display: none;
}

.dashboard-cntrl {
  .menu-web {
    display: flex;
  }
}

.menu-web {
  display: block;
}

.menu-mobile {
  display: none;
}

.executive-analysis {
  svg {
    margin: 18px 16px 10px -5px;
  }
}

.top-menu-name {
  display: none;
}

@include breakpoint(lg-max) {
  .project-detail {
    flex-direction: column;

    .dashboards-aside {
      width: 100% !important;

      .dashboards {
        .dashboard {
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          width: 100%;
          //height: 60px;
          margin-right: 3px;
          background-color: #5597e4;

          i {
            margin-right: 25px;
            margin-left: 20px;
            font-size: 23px;
          }

          small {
            font-size: 18px;
            opacity: 0.8;
          }

          &:hover {
            color: $white !important;

            .store-drilldown-icon {
              &-first-path {
                fill: $white;
              }

              &-second-path {
                stroke: $white;
              }
            }
          }

          &.router-link-active,
          &.router-link-exact-active {
            color: $white;
            background-color: #81b2eb;

            .store-drilldown-icon {
              &-first-path {
                fill: $white;
              }

              &-second-path {
                stroke: $white;
              }
            }
          }
        }

        .top-menu-name {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          width: 100%;
          height: 50px;
          padding: 6px 20px;
          font-weight: 600 !important;
          text-align: center;
          text-decoration: none;

          i {
            margin-top: 5px;
            color: $white;
          }

          small {
            display: inline-block;
            padding: 0 5px;
            font-size: 18px;
            line-height: 13px;
            color: $white;
            opacity: 0.8;
          }
        }
      }
    }

    .filter-aside {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 10;
      width: 100% !important;
      height: 100%;
      overflow-x: hidden;

      .el-form-item__label,
      .el-form-item {
        padding-bottom: 0;
        margin-bottom: 5px;
      }

      button {
        width: 100%;
      }
    }
  }

  .dashboard-cntrl {
    .menu-web {
      display: block;
    }

    .resp-menu {
      display: none;
    }
  }

  .menu-close {
    display: flex;
  }

  .menu-web {
    display: none;
  }

  .menu-mobile {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    display: block;
    width: 100%;
    height: 100%;
    overflow: scroll;
    background: #4a90e2;

    a {
      width: 90% !important;
      margin: 0 0 0 20px;
    }

    .backToProjects {
      margin: 0 0 10px 22px;
      color: $white;

      i {
        font-size: 18px;
      }

      span {
        margin-left: 15px;
        font-size: 18px;
      }
    }
  }

  .resp-menu {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 40px;
    background: $andreaBlue;

    i {
      display: flex;
      justify-items: center;
      font-size: 30px;
      color: $white;
    }
  }

  .executive-analysis {
    svg {
      margin-left: 15px;
    }
  }
}
</style>
